import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Plus, AlertTriangle, Calendar, CalendarDays, Copy, Layers, List, MapPin, MoreVertical, Pencil, Trash2, Upload, UserPlus, XCircle, Download } from 'lucide-react';
import { Alert, AlertDescription, Button, Input, Drawer, Field, FieldLabel, FieldError, Badge, type PaginationState, PaginatedTable, type PaginatedTableColumn } from '../../../../components/ui';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useLessons, useCourts, useCoaches, useLocations } from '../../../../hooks/useDataStore';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import TermScheduleView from '../../../../components/schedule/TermScheduleView';
import {
  DayCourtScheduleView,
  type DayCourtReschedulePayload,
} from '@/components/schedule/DayCourtScheduleView';
import { SessionRescheduleNotifyDialog } from '@/components/schedule/SessionRescheduleNotifyDialog';
import {
  DayColumnAxisToggle,
  persistDayColumnAxis,
  readStoredDayColumnAxis,
  type DayScheduleColumnAxis,
} from '@/components/schedule/DayColumnAxisToggle';
import { commitDayScheduleDrag } from '@/utils/dayScheduleMutations';
import {
  buildAcademyOccurrencesFromLessonSessions,
} from '@/utils/scheduleOccurrences';
import { dateForDayOfWeekInWeek } from '@/utils/scheduleWeek';
import { useLessonCalendarStore } from '@/store/lessonCalendar';
import { isSameDay, startOfWeek } from 'date-fns';
import { TermLessonStudentsDrawer } from './TermLessonStudentsDrawer';
import { CancelTermLessonModal } from '@/components/lessons/CancelTermLessonModal';
import { lessonOverrideService } from '../../../../services/lessonOverride';
import { computeLessonAvailabilityWarningDays, getCoachAvailabilityMissingDays } from '../../../../utils/coachAvailabilityWarnings';
import { AcademySportSelectField } from '@/components/sports/AcademySportSelectField';
import { useAcademySports } from '@/queries/academySports';
import { resolvePrimaryAcademySportId } from '@/utils/sportPresentation';
import { formatSchedulingErrorMessage } from '@/utils/schedulingErrors';

import {
  Lesson,
  Program,
  Term,
  TierFeatureKey,
  doTimesOverlap,
  findCourtHireBookingsConflictingWithLessonSchedule,
  getLessonCourtIds,
  getLessonCoachIds,
  hasEntitlement,
  optionalImageUrlSchema,
  type AgeGroup,
  shouldWarnWhenEditingLessonInRunningSchedule,
  formatDateToFormYmd,
} from '@court-sync/shared';
import { ageGroupService } from '../../../../services/ageGroup';
import { useCoachAcademyFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { programService } from '../../../../services/program';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';
import { getCurrencyFromRegion, getCurrencySymbol } from '../../../../utils/currency';
import { ImportModal, ImportRow, type ImportResult, type ImportContext } from '../../../../components/ImportModal';
import { downloadCSV } from '../../../../utils/csvUtils';
import { toApiLessonTime } from '../../../../utils/timeFormat';
import { lessonMatchesScheduleLocationFilter } from '../../../../utils/lessonLocationFilter';
import { getSessionRescheduleConfirmMeta } from '@/utils/sessionRescheduleUi';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { LessonEditRunningScheduleWarningDialog } from '@/components/lessons/LessonEditRunningScheduleWarningDialog';
import { uploadLessonImage } from '@/services/storage';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { useProgramLessonTemplate } from '@/hooks/useProgramLessonTemplate';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { TERM_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useCourtHireBookingsInRange } from '@/hooks/useCourtHireBookingsInRange';
import { formatInTimeZone } from 'date-fns-tz';

function lessonImportErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
        const msg = data?.error;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
    }
    if (err instanceof Error && err.message) return err.message;
    return '';
}

const DAYS_OF_WEEK = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

type TermLessonsViewMode = 'list' | 'schedule' | 'day';

const TERM_LESSONS_VIEW_STORAGE_KEY = 'courtsync.termLessons.viewMode';

function readStoredTermLessonsView(): TermLessonsViewMode {
    try {
        const stored = localStorage.getItem(TERM_LESSONS_VIEW_STORAGE_KEY);
        if (stored === 'list' || stored === 'schedule' || stored === 'day') {
            return stored;
        }
    } catch {
        /* localStorage unavailable */
    }
    return 'schedule';
}

function persistTermLessonsView(view: TermLessonsViewMode): void {
    try {
        localStorage.setItem(TERM_LESSONS_VIEW_STORAGE_KEY, view);
    } catch {
        /* localStorage unavailable */
    }
}






// Validation Schema
const lessonSchema = z.object({
    name: z.string().min(1, 'Lesson name is required'),
    /** Term lessons are group or private only; camps are managed outside terms. */
    lessonType: z.enum(['group', 'private']),
    description: z.string().optional(),
    daysOfWeek: z.array(z.number().min(0).max(6)).min(1, 'At least one day is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
    price: z.number().min(0, 'Price must be 0 or greater'),
    sessionPrice: z.number().min(0, 'Per-session price must be 0 or greater').optional(),
    maxCapacity: z.number().min(-1, 'Capacity must be at least 1 or -1 for no limit').optional(),
    skillLevel: z.enum(['any', 'beginner', 'intermediate', 'advanced']),
    ageGroup: z.enum(['juniors', 'adults', 'all']),
    ageGroupId: z.union([z.string().uuid(), z.literal('')]).optional().nullable(),
    courtIds: z.array(z.string()).min(1, 'At least one court is required'),
    coachIds: z.array(z.string()).min(1, 'At least one coach is required'),
    isActive: z.boolean(),
    isFeatured: z.boolean(),
    // Private lesson specific
    startDate: z.string().optional(),
    numberOfSessions: z.number().min(1).optional(),
    locationId: z.union([z.string().uuid(), z.literal('')]).optional(),
    /** Optional link to a development program (Professional+). */
    programId: z.union([z.string().uuid(), z.literal('')]).optional(),
    academySportId: z.union([z.string().uuid(), z.literal('')]).optional(),
    imageUrl: optionalImageUrlSchema,
}).refine((data) => {
    if (data.lessonType === 'private') {
        return (
            data.maxCapacity !== undefined &&
            data.maxCapacity !== null &&
            data.maxCapacity >= 1 &&
            data.maxCapacity <= 50
        );
    }
    return data.maxCapacity !== undefined && data.maxCapacity !== null;
}, {
    message: 'Private lessons require capacity between 1 and 50. Group lessons require capacity.',
    path: ['maxCapacity'],
}).refine((data) => {
    if (!data.startTime || !data.endTime) return true;
    const start = new Date(`2000-01-01T${data.startTime}`);
    const end = new Date(`2000-01-01T${data.endTime}`);
    return end > start;
}, {
    message: 'End time must be after start time',
    path: ['endTime'],
});

type LessonFormData = z.infer<typeof lessonSchema>;

const LEGACY_AGE_GROUP_VALUES = ['juniors', 'adults', 'all'] as const;

function normalizeTimeForForm(t: string | undefined): string {
    if (!t) return '';
    const m = t.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return t.trim();
    return `${m[1].padStart(2, '0')}:${m[2]}`;
}

function formatLessonTime(t: string | undefined): string {
    const normalized = normalizeTimeForForm(t);
    if (!normalized) return '—';
    const [h, m] = normalized.split(':');
    const hour = parseInt(h, 10);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${suffix}`;
}

function formatLessonDays(lesson: Lesson): string {
    const legacyDayOfWeek = (lesson as { dayOfWeek?: number }).dayOfWeek;
    const days =
        lesson.daysOfWeek && lesson.daysOfWeek.length > 0
            ? lesson.daysOfWeek
            : legacyDayOfWeek !== undefined
              ? [legacyDayOfWeek]
              : [];
    if (days.length === 0) return '—';
    return days.map((d) => DAYS_OF_WEEK[d]).join(', ');
}

/** Map API lesson to term-lesson form shape (avoids spread + null/NaN failing Zod on change). */
function lessonToTermFormValues(lesson: Lesson): LessonFormData {
    const courtIds = getLessonCourtIds(lesson);
    const coachIds = getLessonCoachIds(lesson);
    const legacyDayOfWeek = (lesson as { dayOfWeek?: number }).dayOfWeek;
    const daysOfWeek =
        lesson.daysOfWeek && lesson.daysOfWeek.length > 0
            ? lesson.daysOfWeek
            : legacyDayOfWeek !== undefined
              ? [legacyDayOfWeek]
              : [1];
    const legacyAge =
        lesson.ageGroup &&
        LEGACY_AGE_GROUP_VALUES.includes(lesson.ageGroup as (typeof LEGACY_AGE_GROUP_VALUES)[number])
            ? lesson.ageGroup
            : 'all';
    const lessonType = lesson.lessonType === 'private' ? 'private' : 'group';

    return {
        lessonType,
        name: lesson.name || '',
        description: lesson.description || '',
        daysOfWeek,
        startTime: normalizeTimeForForm(lesson.startTime),
        endTime: normalizeTimeForForm(lesson.endTime),
        price: typeof lesson.price === 'number' && !Number.isNaN(lesson.price) ? lesson.price : 0,
        sessionPrice: lesson.sessionPrice ?? undefined,
        maxCapacity: lesson.maxCapacity ?? (lessonType === 'private' ? 1 : 6),
        skillLevel: lesson.skillLevel || 'any',
        ageGroup: legacyAge,
        ageGroupId: lesson.ageGroupId || '',
        courtIds,
        coachIds,
        isActive: lesson.isActive !== false,
        isFeatured: lesson.isFeatured || false,
        startDate:
            lessonType === 'private' && lesson.termId
                ? undefined
                : lesson.startDate
                  ? (lesson.startDate instanceof Date
                        ? lesson.startDate
                        : new Date(lesson.startDate)
                    ).toISOString()
                        .split('T')[0]
                  : undefined,
        numberOfSessions:
            lessonType === 'private' && lesson.termId ? undefined : lesson.numberOfSessions ?? undefined,
        locationId: lesson.locationId || '',
        programId: lesson.programId || '',
        academySportId: lesson.academySportId || '',
        imageUrl: lesson.imageUrl || '',
    };
}

function collectFormErrorMessages(errors: Record<string, unknown>): string[] {
    const messages: string[] = [];
    for (const value of Object.values(errors)) {
        if (!value || typeof value !== 'object') continue;
        if ('message' in value && typeof (value as { message?: unknown }).message === 'string') {
            const msg = (value as { message: string }).message;
            if (msg) messages.push(msg);
            continue;
        }
        messages.push(...collectFormErrorMessages(value as Record<string, unknown>));
    }
    return messages;
}

const DEFAULT_LESSON_FORM_VALUES: LessonFormData = {
    lessonType: 'group',
    name: '',
    description: '',
    startTime: '',
    endTime: '',
    price: 0,
    sessionPrice: undefined,
    isActive: true,
    daysOfWeek: [0],
    courtIds: [],
    coachIds: [],
    maxCapacity: 6,
    isFeatured: false,
    skillLevel: 'any',
    ageGroup: 'all',
    ageGroupId: '',
    locationId: '',
    programId: '',
    academySportId: '',
    imageUrl: '',
};

interface TermLessonsProps {
    term: Term;
}

export const TermLessons: React.FC<TermLessonsProps> = ({ term }) => {
    const { currentAcademy } = useCurrentAcademy();
    const { hasValidSubscription } = useSubscriptionCheck();
    const hasMultiLocation = useHasMultiLocation();
    const { hasAccess: hasProgramsAccess } = useCoachAcademyFeatureAccess(
      currentAcademy,
      currentAcademy?.slug ?? null,
      TierFeatureKey.PROGRAMS
    );
    const { locations } = useLocations(currentAcademy?.id, {
      includeInactive: true,
      enabled: hasMultiLocation,
    });
    const { sports: academySports } = useAcademySports(currentAcademy?.id);
    const defaultAcademySportId = resolvePrimaryAcademySportId(academySports);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [academyAgeGroups, setAcademyAgeGroups] = useState<AgeGroup[]>([]);

    useEffect(() => {
        if (!currentAcademy?.id) return;
        ageGroupService.getAgeGroups(currentAcademy.id).then((groups) => {
            setAcademyAgeGroups(groups.filter((g) => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
        }).catch(() => {
            setAcademyAgeGroups([]);
        });
    }, [currentAcademy?.id]);

    useEffect(() => {
        if (!currentAcademy?.id || !hasProgramsAccess) {
            setPrograms([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const list = await programService.getPrograms(currentAcademy.id, true);
                if (!cancelled) setPrograms(list);
            } catch {
                if (!cancelled) setPrograms([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentAcademy?.id, hasProgramsAccess]);

    const programNameById = useMemo(() => {
        const m: Record<string, string> = {};
        for (const p of programs) {
            m[p.id] = p.name;
        }
        return m;
    }, [programs]);

    // Use centralized stores
    const { lessons: allLessons, isLoading: lessonsLoading, createLesson, updateLesson, deleteLesson, refresh: refreshLessons } = useLessons(currentAcademy?.id);
    const { courts, isLoading: courtsLoading } = useCourts(currentAcademy?.id);
    const { coaches, isLoading: coachesLoading } = useCoaches(currentAcademy?.id);

    usePageTourReady(
        'academy-terms-manage',
        !lessonsLoading && !courtsLoading && !coachesLoading
    );

    const [cancelledLessonIds, setCancelledLessonIds] = useState<Set<string>>(new Set());

    const refreshTermCancellations = useCallback(async () => {
        if (!currentAcademy?.id) return;
        try {
            const cancellations = await lessonOverrideService.getTermCancellations(currentAcademy.id);
            const forThisTerm = cancellations.filter((tc) => tc.termId === term.id);
            setCancelledLessonIds(new Set(forThisTerm.map((tc) => tc.lessonId)));
        } catch {
            setCancelledLessonIds(new Set());
        }
    }, [currentAcademy?.id, term.id]);

    useEffect(() => {
        void refreshTermCancellations();
    }, [refreshTermCancellations]);

    // Filter lessons for this term (include cancelled - show with badge)
    const lessons = useMemo(() => {
        return allLessons.filter(l => l.termId === term.id);
    }, [allLessons, term.id]);

    const { user } = useAuthStore();
    const { startPageTour } = usePageTourActions();

    const [showForm, setShowForm] = useState(false);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
    const lessonCreateTourScheduledRef = useRef(false);
    const cancelLessonCreateTourScheduleRef = useRef<(() => void) | null>(null);

    usePageTourReady(
        'academy-terms-lesson-create',
        showForm && !editingLesson && Boolean(currentAcademy)
    );

    const [staffEnrollLesson, setStaffEnrollLesson] = useState<Lesson | null>(null);
    const [cancelLessonTarget, setCancelLessonTarget] = useState<Lesson | null>(null);
    const [cancelLessonSubmitting, setCancelLessonSubmitting] = useState(false);
    const [viewMode, setViewModeState] = useState<TermLessonsViewMode>(readStoredTermLessonsView);
    const [dayColumnAxis, setDayColumnAxisState] = useState<DayScheduleColumnAxis>(readStoredDayColumnAxis);
    const setDayColumnAxis = useCallback((axis: DayScheduleColumnAxis) => {
        setDayColumnAxisState(axis);
        persistDayColumnAxis(axis);
    }, []);
    const [termSelectedDayOfWeek, setTermSelectedDayOfWeek] = useState(() => new Date().getDay());
    const [pendingTermReschedule, setPendingTermReschedule] = useState<DayCourtReschedulePayload | null>(null);
    const [termRescheduleSaving, setTermRescheduleSaving] = useState(false);
    const lessonSessions = useLessonCalendarStore((state) => state.lessonSessions);
    const makeupSessions = useLessonCalendarStore((state) => state.makeupSessions);
    const schedulePendingBookings = useLessonCalendarStore((state) => state.schedulePendingBookings);
    const scheduleEnrollmentBookings = useLessonCalendarStore((state) => state.scheduleEnrollmentBookings);
    const removedFromSessionEnrollments = useLessonCalendarStore(
        (state) => state.removedFromSessionEnrollments
    );
    const reportedAbsenceSessions = useLessonCalendarStore((state) => state.reportedAbsenceSessions);
    const fetchOwnerScheduleForRange = useLessonCalendarStore((state) => state.fetchOwnerScheduleForRange);
    const refreshOwnerScheduleAfterMutation = useLessonCalendarStore(
        (state) => state.refreshOwnerScheduleAfterMutation
    );
    const [lessonsPage, setLessonsPage] = useState(1);
    const lessonsPerPage = 10;
    const setViewMode = useCallback((view: TermLessonsViewMode) => {
        setViewModeState(view);
        persistTermLessonsView(view);
    }, []);
    /** Weekly schedule only: narrow by location when multi-location is enabled */
    const [scheduleLocationFilter, setScheduleLocationFilter] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [pendingRunningScheduleEditLesson, setPendingRunningScheduleEditLesson] = useState<Lesson | null>(null);
    const [conflicts, setConflicts] = useState<{
        lesson?: Lesson;
        type: 'court' | 'coach' | 'coachAvailability' | 'courtHire';
        termName: string;
        courtName: string;
        coachName: string;
        missingDays?: number[];
        courtHireDate?: string;
        courtHireTime?: string;
    }[]>([]);

    // Form setup
    const { register, handleSubmit, reset, watch, setValue, getValues, control, formState: { errors, isValid } } = useForm<LessonFormData>({
        resolver: zodResolver(lessonSchema),
        mode: 'onChange',
        defaultValues: DEFAULT_LESSON_FORM_VALUES,
    });

    const watchedLessonType = watch('lessonType');

    useEffect(() => {
        if (watchedLessonType === 'private') {
            const cap = getValues('maxCapacity');
            if (cap === undefined || cap === null || cap < 1) {
                setValue('maxCapacity', 1, { shouldValidate: true });
            }
            setValue('startDate', undefined, { shouldValidate: true });
            setValue('numberOfSessions', undefined, { shouldValidate: true });
        }
    }, [watchedLessonType, setValue, getValues]);

    const selectedDaysOfWeek = watch('daysOfWeek') || [0];
    const watchedStartTime = watch('startTime');
    const watchedEndTime = watch('endTime');

    const watchedCourtIds = watch('courtIds');
    const watchedCoachIds = watch('coachIds');
    const watchedMaxCapacity = watch('maxCapacity');
    const watchedLocationId = watch('locationId') || '';
    const watchedAcademySportId = watch('academySportId') || '';
    const watchedImageUrl = watch('imageUrl') || '';
    const watchedProgramId = watch('programId') || '';

    const {
        displayUrl: coverDisplayUrl,
        uploading: uploadingCover,
        handleFileSelect: handleCoverFile,
        handleRemove: handleCoverRemove,
        clear: clearCoverUi,
        resolveImagePayloadForSubmit: resolveCoverImagePayload,
    } = useCoverImageUpload({
        academyId: currentAcademy?.id,
        entityId: editingLesson?.id,
        imageUrl: watchedImageUrl,
        setImageUrl: (url) => setValue('imageUrl', url, { shouldDirty: true }),
        upload: uploadLessonImage,
    });

    const { applyProgramTemplate, helperText: programTemplateHelperText } = useProgramLessonTemplate({
        programs,
        setValue,
        clearCoverUi,
        emptyProgramId: '',
    });

    const programsForLessonForm = useMemo(() => {
        const sportId = watchedAcademySportId || defaultAcademySportId;
        const selectedId = watchedProgramId;
        if (!sportId) return programs;
        return programs.filter(
            (p) => !p.academySportId || p.academySportId === sportId || p.id === selectedId
        );
    }, [programs, watchedAcademySportId, defaultAcademySportId, watchedProgramId]);

    useEffect(() => {
        if (!showForm || editingLesson || !defaultAcademySportId) return;
        const current = getValues('academySportId');
        if (!current) {
            setValue('academySportId', defaultAcademySportId, { shouldValidate: true });
        }
    }, [showForm, editingLesson, defaultAcademySportId, getValues, setValue]);

    const formValidationMessages = useMemo(
        () => collectFormErrorMessages(errors as Record<string, unknown>),
        [errors]
    );

    const activeCourtsForPicker = useMemo(() => {
        const active = courts.filter((c) => c.isActive);
        if (!hasMultiLocation || locations.length === 0 || !watchedLocationId) {
            return active;
        }
        return active.filter((c) => c.locationId === watchedLocationId);
    }, [courts, hasMultiLocation, locations.length, watchedLocationId]);

    useEffect(() => {
        if (!showForm || !hasMultiLocation || locations.length === 0) return;
        const ids = getValues('courtIds') || [];
        const allowed = new Set(activeCourtsForPicker.map((c) => c.id));
        const next = ids.filter((id) => allowed.has(id));
        if (next.length !== ids.length) {
            setValue('courtIds', next, { shouldValidate: true });
        }
    }, [watchedLocationId, showForm, hasMultiLocation, locations.length, activeCourtsForPicker, setValue, getValues]);

    const selectedCourtIds = useMemo(() => watchedCourtIds || [], [watchedCourtIds]);
    const selectedCoachIds = useMemo(() => watchedCoachIds || [], [watchedCoachIds]);
    const activeCoaches = useMemo(() => coaches.filter(c => c.isActive), [coaches]);

    const { currentClub } = useCurrentClub();
    const { entitlements } = useClubModuleAccess();
    const courtHireEntitled = Boolean(
        entitlements && hasEntitlement(entitlements, TierFeatureKey.COURT_HIRE)
    );
    const termStartDate = formatDateToFormYmd(term.startDate);
    const termEndDate = formatDateToFormYmd(term.endDate);
    const { bookings: courtHireBookings } = useCourtHireBookingsInRange({
        clubId: currentClub?.id,
        from: termStartDate,
        to: termEndDate,
        enabled: showForm && courtHireEntitled,
    });
    const clubTimeZone = currentClub?.timezone || currentAcademy?.timezone || 'UTC';

    // Academy currency
    const academyCurrency = currentAcademy?.currency || getCurrencyFromRegion(currentAcademy?.region);
    const currencySymbol = getCurrencySymbol(academyCurrency);

    useEffect(() => {
        if (!showForm || submitting || !selectedDaysOfWeek || selectedDaysOfWeek.length === 0 || !watchedStartTime || !watchedEndTime) {
            if (conflicts.length > 0) {
                setConflicts([]);
            }
            return;
        }

        const detectedConflicts: any[] = [];

        // Only check conflicts within the current term - lessons in other terms occur at different calendar periods
        const lessonsInThisTerm = allLessons.filter((l) => l.termId === term.id);

        lessonsInThisTerm.forEach(otherLesson => {
            // Skip if same lesson (when editing)
            if (otherLesson.id === editingLesson?.id) return;

            const otherLessonDays = otherLesson.daysOfWeek || ((otherLesson as any).dayOfWeek !== undefined ? [(otherLesson as any).dayOfWeek] : []);
            const hasDayConflict = otherLessonDays.some(day => selectedDaysOfWeek.includes(day));

            if (!hasDayConflict) return;

            if (doTimesOverlap(watchedStartTime, watchedEndTime, otherLesson.startTime, otherLesson.endTime)) {
                // Court conflict - other lesson uses any of our selected courts
                const conflictingCourtId = getLessonCourtIds(otherLesson).find(cid => selectedCourtIds.includes(cid));
                if (conflictingCourtId) {
                    detectedConflicts.push({
                        type: 'court',
                        lesson: otherLesson,
                        termName: 'Other Term',
                        courtName: courts.find(c => c.id === conflictingCourtId)?.name || 'Unknown',
                        coachName: coaches.find(c => c.id === getLessonCoachIds(otherLesson)[0])?.firstName || 'Unknown',
                    });
                }
                // Coach conflict - other lesson uses any of our selected coaches
                const conflictingCoachId = getLessonCoachIds(otherLesson).find(cid => selectedCoachIds.includes(cid));
                if (conflictingCoachId) {
                    detectedConflicts.push({
                        type: 'coach',
                        lesson: otherLesson,
                        termName: 'Other Term',
                        courtName: courts.find(c => c.id === getLessonCourtIds(otherLesson)[0])?.name || 'Unknown',
                        coachName: coaches.find(c => c.id === conflictingCoachId)?.firstName || 'Unknown',
                    });
                }
            }
        });

        // Coach availability warnings (non-blocking, shown in conflict panel)
        selectedCoachIds.forEach((coachId) => {
            const coach = coaches.find((c) => c.id === coachId) as (typeof coaches[number] & {
                availability?: Array<{ dayOfWeek: number; startTime: string; endTime: string; isAvailable: boolean }>;
            }) | undefined;
            const missingDays = getCoachAvailabilityMissingDays(
                coach,
                selectedDaysOfWeek,
                watchedStartTime,
                watchedEndTime
            );
            if (missingDays.length === 0) return;

            detectedConflicts.push({
                type: 'coachAvailability',
                termName: term.name,
                courtName: 'N/A',
                coachName: coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach',
                missingDays,
            });
        });

        if (selectedCourtIds.length > 0 && courtHireBookings.length > 0) {
            const conflictingBookings = findCourtHireBookingsConflictingWithLessonSchedule(
                courtHireBookings,
                {
                    courtIds: selectedCourtIds,
                    daysOfWeek: selectedDaysOfWeek,
                    startTime: watchedStartTime,
                    endTime: watchedEndTime,
                    dateRange: { startDate: termStartDate, endDate: termEndDate },
                    timeZone: clubTimeZone,
                }
            );

            conflictingBookings.forEach((booking) => {
                const courtName = courts.find((court) => court.id === booking.courtId)?.name || 'Unknown Court';
                const bookingStart = new Date(booking.startAt);

                detectedConflicts.push({
                    type: 'courtHire',
                    termName: term.name,
                    courtName,
                    coachName: 'Court hire',
                    courtHireDate: Number.isNaN(bookingStart.getTime())
                        ? ''
                        : formatInTimeZone(bookingStart, clubTimeZone, 'd MMM yyyy'),
                    courtHireTime: Number.isNaN(bookingStart.getTime())
                        ? ''
                        : formatInTimeZone(bookingStart, clubTimeZone, 'HH:mm'),
                });
            });
        }

        // Deep compare conflicts to avoid infinite loop if reference changes but content is same
        if (JSON.stringify(detectedConflicts) !== JSON.stringify(conflicts)) {
            setConflicts(detectedConflicts);
        }
    }, [selectedDaysOfWeek.join(','), watchedStartTime, watchedEndTime, selectedCourtIds, selectedCoachIds, allLessons, term.id, termStartDate, termEndDate, term.name, showForm, editingLesson, submitting, conflicts, courts, coaches, courtHireBookings, clubTimeZone]);

    const activeLocations = useMemo(
        () => locations.filter((l) => l.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
        [locations]
    );

    const scheduleLocationFilterLabel = useMemo(() => {
        if (!scheduleLocationFilter) return 'All locations';
        return activeLocations.find((loc) => loc.id === scheduleLocationFilter)?.name ?? 'Location';
    }, [scheduleLocationFilter, activeLocations]);

    const scheduleViewLessons = useMemo(() => {
        if (!hasMultiLocation || activeLocations.length === 0 || !scheduleLocationFilter) {
            return lessons;
        }
        return lessons.filter((l) =>
            lessonMatchesScheduleLocationFilter(l, scheduleLocationFilter, courts)
        );
    }, [lessons, hasMultiLocation, activeLocations.length, scheduleLocationFilter, courts]);

    const lessonAvailabilityWarnings = useMemo(() => {
        return computeLessonAvailabilityWarningDays(scheduleViewLessons, coaches);
    }, [scheduleViewLessons, coaches]);

    const courtNamesById = useMemo(
        () => Object.fromEntries(courts.map((c) => [c.id, c.name] as const)),
        [courts]
    );

    const scheduleCourts = useMemo(() => {
        let list = courts.filter((c) => c.isActive !== false);
        if (hasMultiLocation && scheduleLocationFilter) {
            list = list.filter((c) => c.locationId === scheduleLocationFilter);
        }
        return list;
    }, [courts, hasMultiLocation, scheduleLocationFilter]);

    const termPlanningWeekStart = useMemo(
        () => startOfWeek(new Date(), { weekStartsOn: 1 }),
        []
    );

    const termPlanningDate = useMemo(
        () => dateForDayOfWeekInWeek(termPlanningWeekStart, termSelectedDayOfWeek),
        [termPlanningWeekStart, termSelectedDayOfWeek]
    );

    const termDayOccurrences = useMemo(() => {
        const all = buildAcademyOccurrencesFromLessonSessions(
            scheduleViewLessons,
            [term],
            lessonSessions,
            makeupSessions,
            schedulePendingBookings,
            reportedAbsenceSessions,
            courtNamesById,
            undefined,
            'active',
            scheduleEnrollmentBookings,
            removedFromSessionEnrollments
        );
        return all.filter((occ) => isSameDay(occ.date, termPlanningDate));
    }, [
        scheduleViewLessons,
        term,
        lessonSessions,
        makeupSessions,
        schedulePendingBookings,
        reportedAbsenceSessions,
        courtNamesById,
        scheduleEnrollmentBookings,
        removedFromSessionEnrollments,
        termPlanningDate,
    ]);

    useEffect(() => {
        if (!currentAcademy?.id || viewMode !== 'day') return;
        const start = term.startDate instanceof Date ? term.startDate : new Date(term.startDate);
        const end = term.endDate instanceof Date ? term.endDate : new Date(term.endDate);
        void fetchOwnerScheduleForRange(currentAcademy.id, start, end);
    }, [currentAcademy?.id, viewMode, term, fetchOwnerScheduleForRange]);

    const commitTermReschedule = useCallback(
        async (
            payload: DayCourtReschedulePayload,
            notifyGuardians: boolean,
            reason?: string
        ) => {
            if (!currentAcademy?.id || !payload.occurrence.sessionId) {
                toast.error('This session cannot be rescheduled yet');
                return;
            }
            setTermRescheduleSaving(true);
            try {
                await commitDayScheduleDrag({
                    payload,
                    academyId: currentAcademy.id,
                    notifyGuardians,
                    reason,
                });
                await refreshOwnerScheduleAfterMutation(currentAcademy.id);
                toast.success('Session updated');
                setPendingTermReschedule(null);
            } catch (e: unknown) {
                toast.error(formatSchedulingErrorMessage(e, 'Failed to reschedule session'));
            } finally {
                setTermRescheduleSaving(false);
            }
        },
        [currentAcademy?.id, refreshOwnerScheduleAfterMutation]
    );

    const handleTermOccurrenceReschedule = useCallback(
        (payload: DayCourtReschedulePayload) => {
            const { needsConfirmDialog } = getSessionRescheduleConfirmMeta(payload, currentAcademy);
            if (needsConfirmDialog) {
                setPendingTermReschedule(payload);
                return;
            }
            void commitTermReschedule(payload, false);
        },
        [commitTermReschedule, currentAcademy]
    );

    const pendingTermRescheduleMeta = pendingTermReschedule
        ? getSessionRescheduleConfirmMeta(pendingTermReschedule, currentAcademy)
        : null;

    const lessonsTotalPages = Math.max(1, Math.ceil(lessons.length / lessonsPerPage));
    const paginatedLessons = useMemo(() => {
        const start = (lessonsPage - 1) * lessonsPerPage;
        return lessons.slice(start, start + lessonsPerPage);
    }, [lessons, lessonsPage, lessonsPerPage]);

    useEffect(() => {
        setLessonsPage(1);
    }, [term.id]);

    useEffect(() => {
        if (lessonsPage > lessonsTotalPages) setLessonsPage(lessonsTotalPages);
    }, [lessonsPage, lessonsTotalPages]);

    const onSubmit: SubmitHandler<LessonFormData> = async (data) => {
        if (!currentAcademy?.id) return;

        try {
            setSubmitting(true);

            const startTime = new Date(`2000-01-01 ${data.startTime}`);
            const endTime = new Date(`2000-01-01 ${data.endTime}`);
            if (endTime <= startTime) {
                throw new Error('End time must be after start time');
            }

            const locationPayload = hasMultiLocation
                ? { locationId: data.locationId && data.locationId.length > 0 ? data.locationId : null }
                : {};

            const programIdNormalized =
                data.programId && String(data.programId).trim().length > 0 ? data.programId : null;
            const ageGroupIdNormalized =
                data.ageGroupId && String(data.ageGroupId).trim().length > 0 ? data.ageGroupId : undefined;

            const normalizedSessionPrice =
                (data.lessonType === 'group' || data.lessonType === 'private') &&
                data.sessionPrice != null &&
                !Number.isNaN(data.sessionPrice) &&
                data.sessionPrice >= 0
                    ? data.sessionPrice
                    : null;

            const academySportIdNormalized =
                data.academySportId && String(data.academySportId).trim().length > 0
                    ? data.academySportId
                    : defaultAcademySportId;

            const sharedFields = {
                academyId: currentAcademy.id,
                name: data.name,
                description: data.description || undefined,
                courtIds: data.courtIds,
                coachIds: data.coachIds,
                skillLevel: data.skillLevel,
                ageGroup: data.ageGroup,
                ageGroupId: ageGroupIdNormalized,
                daysOfWeek: data.daysOfWeek,
                startTime: data.startTime,
                endTime: data.endTime,
                price: data.price,
                currency: academyCurrency,
                isActive: data.isActive,
                isFeatured: data.isFeatured,
                programId: programIdNormalized,
                academySportId: academySportIdNormalized,
                ...locationPayload,
            };

            if (editingLesson) {
                const imagePayload = resolveCoverImagePayload();
                const lessonData: Record<string, unknown> =
                    data.lessonType === 'private'
                        ? {
                              ...sharedFields,
                              lessonType: 'private',
                              termId: term.id,
                              maxCapacity: data.maxCapacity ?? 1,
                              ...(normalizedSessionPrice != null
                                  ? { sessionPrice: normalizedSessionPrice }
                                  : {}),
                          }
                        : {
                              ...sharedFields,
                              lessonType: 'group',
                              termId: term.id,
                              maxCapacity: data.maxCapacity!,
                              sessionPrice: normalizedSessionPrice,
                          };
                Object.assign(lessonData, imagePayload);
                await updateLesson(editingLesson.id, lessonData as any);
                toast.success('Lesson updated successfully');
                await refreshLessons();
                handleCancelEdit();
            } else {
                const imagePayload = resolveCoverImagePayload();
                const createPayload: Record<string, unknown> =
                    data.lessonType === 'private'
                        ? {
                              ...sharedFields,
                              lessonType: 'private',
                              termIds: [term.id],
                              maxCapacity: data.maxCapacity ?? 1,
                              ...(normalizedSessionPrice != null
                                  ? { sessionPrice: normalizedSessionPrice }
                                  : {}),
                          }
                        : {
                              ...sharedFields,
                              lessonType: 'group',
                              termIds: [term.id],
                              maxCapacity: data.maxCapacity!,
                              ...(normalizedSessionPrice != null ? { sessionPrice: normalizedSessionPrice } : {}),
                          };
                Object.assign(createPayload, imagePayload);
                await createLesson(createPayload as any);
                await refreshLessons();
                toast.success('Lesson created successfully');
                handleCancelEdit();
            }
        } catch (error: unknown) {
            toast.error(formatSchedulingErrorMessage(error, 'Error saving lesson'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (lesson: Lesson) => {
        clearCoverUi();
        setEditingLesson(lesson);
        setShowForm(true);
        reset(lessonToTermFormValues(lesson));
    };

    const offerEditLesson = (lesson: Lesson) => {
        if (shouldWarnWhenEditingLessonInRunningSchedule(lesson, term, new Date())) {
            setPendingRunningScheduleEditLesson(lesson);
            return;
        }
        handleEdit(lesson);
    };

    const lessonHasEnrollments = useCallback(
        (lesson: Lesson) => (lesson.currentEnrollment ?? 0) > 0,
        []
    );

    const handleDelete = async (lesson: Lesson) => {
        if (lessonHasEnrollments(lesson)) {
            toast.error(
                'This lesson has enrolled students. Cancel the lesson or remove all students before deleting.'
            );
            return;
        }
        if (window.confirm('Are you sure you want to delete this lesson?')) {
            try {
                await deleteLesson(lesson.id);
                toast.success('Lesson deleted successfully');
                refreshLessons();
            } catch {
                toast.error('Error deleting lesson');
            }
        }
    };

    const handleCancelLesson = useCallback((lesson: Lesson) => {
        setCancelLessonTarget(lesson);
    }, []);

    const handleCancelTermConfirm = async (
        reason: string,
        notifyStudents: boolean,
        grantMakeupCredit: boolean
    ) => {
        if (!cancelLessonTarget || !currentAcademy?.id) return;
        setCancelLessonSubmitting(true);
        try {
            await lessonOverrideService.cancelTermLesson(
                cancelLessonTarget.id,
                currentAcademy.id,
                reason,
                undefined,
                notifyStudents,
                grantMakeupCredit
            );
            toast.success('Term lesson cancelled.');
            setCancelLessonTarget(null);
            await refreshLessons();
            await refreshTermCancellations();
        } catch (error: unknown) {
            toast.error(error instanceof Error ? error.message : 'Failed to cancel lesson');
        } finally {
            setCancelLessonSubmitting(false);
        }
    };

    const handleStaffEnrollFromSchedule = useCallback((lesson: Lesson) => {
        setStaffEnrollLesson(lesson);
    }, []);

    const handleDuplicateLesson = useCallback(
        async (sourceLesson: Lesson) => {
            if (!currentAcademy?.id || !hasValidSubscription) return;
            try {
                const courtIds = getLessonCourtIds(sourceLesson).filter(Boolean);
                const coachIds = getLessonCoachIds(sourceLesson).filter(Boolean);
                if (courtIds.length === 0 || coachIds.length === 0) {
                    toast.error(
                        'Cannot duplicate lesson: source lesson must have at least one court and one coach assigned'
                    );
                    return;
                }
                const legacyDayOfWeek = (sourceLesson as { dayOfWeek?: number }).dayOfWeek;
                const daysOfWeek =
                    sourceLesson.daysOfWeek && sourceLesson.daysOfWeek.length > 0
                        ? sourceLesson.daysOfWeek
                        : legacyDayOfWeek !== undefined
                          ? [legacyDayOfWeek]
                          : [1];
                const baseName = sourceLesson.name.replace(/\s*\(duplicate\)$/i, '').trim();
                const payload: Record<string, unknown> = {
                    academyId: currentAcademy.id,
                    termIds: [term.id],
                    courtIds,
                    coachIds,
                    name: `${baseName} (duplicate)`,
                    description: sourceLesson.description,
                    lessonType: sourceLesson.lessonType === 'private' ? 'private' : 'group',
                    skillLevel: sourceLesson.skillLevel || 'any',
                    ageGroup: sourceLesson.ageGroup || 'all',
                    ageGroupId: sourceLesson.ageGroupId ?? null,
                    daysOfWeek,
                    startTime: sourceLesson.startTime,
                    endTime: sourceLesson.endTime,
                    maxCapacity: sourceLesson.maxCapacity,
                    price: sourceLesson.price,
                    currency: sourceLesson.currency ?? academyCurrency,
                    isActive: sourceLesson.isActive ?? true,
                    isFeatured: sourceLesson.isFeatured,
                    programId: sourceLesson.programId ?? null,
                    academySportId: sourceLesson.academySportId ?? defaultAcademySportId,
                    ...(sourceLesson.locationId ? { locationId: sourceLesson.locationId } : {}),
                    ...(sourceLesson.imageUrl ? { imageUrl: sourceLesson.imageUrl } : {}),
                };
                if (sourceLesson.sessionPrice != null) {
                    payload.sessionPrice = sourceLesson.sessionPrice;
                }
                await createLesson(payload as any);
                toast.success('Lesson duplicated');
                await refreshLessons();
            } catch (e: unknown) {
                toast.error(e instanceof Error ? e.message : 'Failed to duplicate lesson');
            }
        },
        [
            currentAcademy?.id,
            hasValidSubscription,
            term.id,
            academyCurrency,
            defaultAcademySportId,
            createLesson,
            refreshLessons,
        ]
    );

    const closeStaffEnrollDrawer = useCallback(() => {
        setStaffEnrollLesson(null);
    }, []);

    const lessonTableColumns: PaginatedTableColumn<Lesson>[] = useMemo(
        () => [
            {
                key: 'name',
                header: 'Lesson',
                render: (lesson) => {
                    const isTermCancelled = !!(lesson.cancelledFromDate || cancelledLessonIds.has(lesson.id));
                    return (
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">{lesson.name}</span>
                                <Badge variant="secondary" className="uppercase text-[10px]">
                                    {lesson.lessonType}
                                </Badge>
                                {lesson.programId && programNameById[lesson.programId] && (
                                    <Badge
                                        variant="outline"
                                        className="text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800"
                                    >
                                        <Layers className="h-3 w-3 mr-1 shrink-0" aria-hidden />
                                        {programNameById[lesson.programId]}
                                    </Badge>
                                )}
                            </div>
                            {lesson.skillLevel && lesson.skillLevel !== 'any' && (
                                <p className="mt-0.5 text-xs capitalize text-muted-foreground">{lesson.skillLevel}</p>
                            )}
                            {isTermCancelled && lesson.cancelledReason && (
                                <p className="mt-0.5 line-clamp-1 text-xs text-amber-700 dark:text-amber-300">
                                    {lesson.cancelledReason}
                                </p>
                            )}
                        </div>
                    );
                },
            },
            {
                key: 'schedule',
                header: 'Schedule',
                render: (lesson) => (
                    <span className="text-sm text-muted-foreground">
                        {formatLessonDays(lesson)} · {formatLessonTime(lesson.startTime)}–
                        {formatLessonTime(lesson.endTime)}
                    </span>
                ),
            },
            {
                key: 'court',
                header: 'Court',
                render: (lesson) => (
                    <span className="text-sm text-muted-foreground">
                        {getLessonCourtIds(lesson)
                            .map((id) => courts.find((c) => c.id === id)?.name || 'Unknown')
                            .join(', ') || '—'}
                    </span>
                ),
            },
            {
                key: 'enrollment',
                header: 'Enrolled',
                render: (lesson) => (
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {lesson.maxCapacity === -1
                            ? `${lesson.currentEnrollment ?? 0} (No limit)`
                            : `${lesson.currentEnrollment ?? 0}/${lesson.maxCapacity}`}
                    </span>
                ),
            },
            {
                key: 'price',
                header: 'Price',
                render: (lesson) => (
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {currencySymbol}{lesson.price}
                        {lesson.sessionPrice != null ? (
                            <span className="block text-xs">
                                {currencySymbol}{lesson.sessionPrice} / session
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                render: (lesson) => {
                    const isTermCancelled = !!(lesson.cancelledFromDate || cancelledLessonIds.has(lesson.id));
                    if (isTermCancelled) {
                        return <Badge variant="secondary">Cancelled</Badge>;
                    }
                    return lesson.isActive ? (
                        <Badge variant="success">Active</Badge>
                    ) : (
                        <Badge variant="secondary">Inactive</Badge>
                    );
                },
            },
            {
                key: 'actions',
                header: <span className="sr-only">Actions</span>,
                className: 'text-right w-[1%] whitespace-nowrap',
                render: (lesson) => {
                    const isTermCancelled = !!(lesson.cancelledFromDate || cancelledLessonIds.has(lesson.id));
                    if (isTermCancelled) return null;
                    return (
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                onClick={() => offerEditLesson(lesson)}
                                aria-label="Edit lesson"
                            >
                                <Pencil className="h-4 w-4" aria-hidden />
                            </Button>
                            {hasValidSubscription && (
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={() => handleStaffEnrollFromSchedule(lesson)}
                                    aria-label="Manage students"
                                >
                                    <UserPlus className="h-4 w-4" aria-hidden />
                                </Button>
                            )}
                            <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                onClick={() => handleDuplicateLesson(lesson)}
                                aria-label="Duplicate lesson"
                            >
                                <Copy className="h-4 w-4" aria-hidden />
                            </Button>
                            {lessonHasEnrollments(lesson) ? (
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={() => handleCancelLesson(lesson)}
                                    className="text-amber-700 hover:text-amber-700 dark:text-amber-300"
                                    aria-label="Cancel lesson"
                                >
                                    <XCircle className="h-4 w-4" aria-hidden />
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={() => handleDelete(lesson)}
                                    className="text-red-600 hover:text-red-700 dark:text-red-400"
                                    aria-label="Delete lesson"
                                >
                                    <Trash2 className="h-4 w-4" aria-hidden />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            cancelledLessonIds,
            courts,
            currencySymbol,
            handleCancelLesson,
            handleDelete,
            handleDuplicateLesson,
            handleStaffEnrollFromSchedule,
            hasValidSubscription,
            lessonHasEnrollments,
            offerEditLesson,
            programNameById,
        ]
    );

    const handleCancelEdit = () => {
        clearCoverUi();
        setEditingLesson(null);
        setShowForm(false);
        reset(DEFAULT_LESSON_FORM_VALUES);
    };

    const openNewLessonForm = useCallback(() => {
        clearCoverUi();
        setEditingLesson(null);
        reset(DEFAULT_LESSON_FORM_VALUES);
        setShowForm(true);
    }, [clearCoverUi, reset]);

    useEffect(() => {
        if (!showForm) {
            lessonCreateTourScheduledRef.current = false;
            cancelLessonCreateTourScheduleRef.current?.();
            cancelLessonCreateTourScheduleRef.current = null;
        }
    }, [showForm]);

    const handleLessonCreateDrawerPanelOpen = useCallback(() => {
        if (
            editingLesson ||
            lessonCreateTourScheduledRef.current ||
            isPageTourComplete(user?.preferences, 'academy-terms-lesson-create')
        ) {
            return;
        }
        lessonCreateTourScheduledRef.current = true;
        cancelLessonCreateTourScheduleRef.current?.();
        cancelLessonCreateTourScheduleRef.current = scheduleAutoPageTour(
            startPageTour,
            'academy-terms-lesson-create',
            user?.preferences
        );
    }, [editingLesson, startPageTour, user?.preferences]);

    const handleCreateFromScheduleSlot = ({
        dayOfWeek,
        startTime,
        endTime,
        courtId,
    }: {
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        courtId?: string;
    }) => {
        const schedulableCourtId =
            courtId && courts.some((court) => court.id === courtId) ? courtId : undefined;
        setEditingLesson(null);
        reset({
            ...DEFAULT_LESSON_FORM_VALUES,
            daysOfWeek: [dayOfWeek],
            startTime,
            endTime,
            locationId: '',
            ...(schedulableCourtId ? { courtIds: [schedulableCourtId] } : {}),
        });
        setShowForm(true);
    };

    const toggleDaySelection = (day: number) => {
        const current = watch('daysOfWeek') || [];
        if (current.includes(day)) {
            setValue('daysOfWeek', current.filter(d => d !== day), { shouldDirty: true, shouldValidate: true });
        } else {
            setValue('daysOfWeek', [...current, day], { shouldDirty: true, shouldValidate: true });
        }
    };

    const toggleSelection = (id: string, field: 'courtIds' | 'coachIds') => {
        const current = watch(field) || [];
        if (current.includes(id)) {
            setValue(field, current.filter(i => i !== id), { shouldDirty: true });
        } else {
            setValue(field, [...current, id], { shouldDirty: true });
        }
    };

    const handleExportLessons = () => {
        const exportData = lessons.map(lesson => {
            const gid = lesson.ageGroupId;
            const customLabel = gid
                ? academyAgeGroups.find((g) => g.id === gid)?.label ?? ''
                : '';
            return {
              name: lesson.name,
              lessonType: lesson.lessonType,
              daysOfWeek: (lesson.daysOfWeek || []).join(','),
              startTime: toApiLessonTime(lesson.startTime),
              endTime: toApiLessonTime(lesson.endTime),
              maxCapacity: lesson.maxCapacity,
              price: lesson.price,
              skillLevel: lesson.skillLevel || 'any',
              ageGroup: lesson.ageGroup || 'all',
              ageGroupId: gid ?? '',
              ageGroupLabel: customLabel,
              isFeatured: lesson.isFeatured ? 'true' : 'false',
              courtNames: getLessonCourtIds(lesson)
                .map((id) => courts.find((c) => c.id === id)?.name || '')
                .join('; '),
              coachNames: getLessonCoachIds(lesson)
                .map((id) => {
                  const coach = coaches.find((c) => c.id === id);
                  return coach ? `${coach.firstName} ${coach.lastName}` : '';
                })
                .join('; '),
            };
        });
        downloadCSV(`lessons-${term.name}-${new Date().toISOString().split('T')[0]}.csv`, exportData);
        toast.success(`Exported ${lessons.length} lesson${lessons.length !== 1 ? 's' : ''}`);
    };

    const handleImportLessons = async (
        rows: ImportRow[],
        ctx?: ImportContext
    ): Promise<ImportResult | undefined> => {
        if (!currentAcademy?.id) {
            toast.error('Academy context is not ready. Refresh the page and try again.');
            return undefined;
        }

        let createdCount = 0;
        let failedCount = 0;
        let firstError = '';
        const totalRows = rows.length;

        const findCourtIdByName = (name: string): string | undefined => {
            const n = name.trim().toLowerCase();
            return courts.find((c) => c.name.trim().toLowerCase() === n)?.id;
        };

        const findCoachIdByDisplayName = (raw: string): string | undefined => {
            const normalized = raw.trim().toLowerCase().replace(/\s+/g, ' ');
            if (!normalized) return undefined;
            return coaches.find((c) => {
                const display = `${c.firstName} ${c.lastName}`.trim().toLowerCase().replace(/\s+/g, ' ');
                return display === normalized;
            })?.id;
        };

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
            const row = rows[rowIndex]!;
            ctx?.reportProgress(rowIndex, totalRows);

            if (!row.isValid || !row.data.name || !row.data.lessonType) {
                failedCount++;
                continue;
            }

            const rawType = (row.data.lessonType || '').trim().toLowerCase();
            try {
                const daysOfWeek = (row.data.daysOfWeek || '')
                    .split(',')
                    .map(d => parseInt(d.trim(), 10))
                    .filter(d => !isNaN(d) && d >= 0 && d <= 6);

                if (daysOfWeek.length === 0) {
                    failedCount++;
                    continue;
                }

                // Match court names to IDs
                const courtNames = (row.data.courtNames || '')
                    .split(';')
                    .map(n => n.trim())
                    .filter(n => n.length > 0);
                const courtIds = courtNames
                    .map((name) => findCourtIdByName(name))
                    .filter((id): id is string => !!id);

                if (courtIds.length === 0) {
                    failedCount++;
                    continue;
                }

                // Match coach names to IDs
                const coachNames = (row.data.coachNames || '')
                    .split(';')
                    .map(n => n.trim())
                    .filter(n => n.length > 0);
                const coachIds = coachNames
                    .map((name) => findCoachIdByDisplayName(name))
                    .filter((id): id is string => !!id);

                if (coachIds.length === 0) {
                    failedCount++;
                    continue;
                }

                if (rawType === 'camp') {
                    failedCount++;
                    continue;
                }

                let lessonTypeForRow: 'group' | 'private';
                if (rawType === 'private') {
                    lessonTypeForRow = 'private';
                } else if (rawType === 'group' || rawType === 'clinic') {
                    lessonTypeForRow = 'group';
                } else {
                    failedCount++;
                    continue;
                }

                const uuidRaw = (row.data.ageGroupId || '').trim();
                const ageGroupRaw = (row.data.ageGroup || '').trim();

                let resolvedAgeGroupId: string | undefined;
                let legacyAgeGroup: 'juniors' | 'adults' | 'all' = 'all';

                if (uuidRaw) {
                    const foundById = academyAgeGroups.find((g) => g.id === uuidRaw);
                    if (foundById) {
                        resolvedAgeGroupId = foundById.id;
                        legacyAgeGroup = 'all';
                    } else {
                        failedCount++;
                        if (!firstError) firstError = `Unknown ageGroupId "${uuidRaw}"`;
                        continue;
                    }
                } else if (ageGroupRaw) {
                    const foundByLabel = academyAgeGroups.find(
                        (g) => g.label.trim().toLowerCase() === ageGroupRaw.toLowerCase()
                    );
                    if (foundByLabel) {
                        resolvedAgeGroupId = foundByLabel.id;
                        legacyAgeGroup = 'all';
                    } else if (['juniors', 'adults', 'all'].includes(ageGroupRaw.toLowerCase())) {
                        legacyAgeGroup = ageGroupRaw.toLowerCase() as 'juniors' | 'adults' | 'all';
                    } else {
                        failedCount++;
                        if (!firstError) {
                            firstError = `ageGroup "${ageGroupRaw}" is not a known label or juniors/adults/all`;
                        }
                        continue;
                    }
                }

                await createLesson({
                    academyId: currentAcademy.id,
                    termIds: [term.id],
                    name: row.data.name,
                    lessonType: lessonTypeForRow,
                    daysOfWeek,
                    startTime: toApiLessonTime(row.data.startTime),
                    endTime: toApiLessonTime(row.data.endTime),
                    maxCapacity: parseInt(row.data.maxCapacity || '-1', 10),
                    price: parseFloat(row.data.price || '0'),
                    currency: academyCurrency,
                    skillLevel: row.data.skillLevel as any,
                    ageGroup: legacyAgeGroup,
                    ...(resolvedAgeGroupId ? { ageGroupId: resolvedAgeGroupId } : {}),
                    courtIds,
                    coachIds,
                    isActive: true,
                    isFeatured: row.data.isFeatured === 'true',
                } as any);
                createdCount++;
            } catch (error: any) {
                console.error(`Failed to create lesson at row ${row.rowNumber}:`, error);
                failedCount++;
                if (!firstError) {
                    firstError = lessonImportErrorMessage(error);
                }
            }
        }

        ctx?.reportProgress(totalRows, totalRows);

        if (createdCount === 0 && failedCount > 0) {
            toast.error(
                firstError
                    ? `${firstError} (${failedCount} row${failedCount !== 1 ? 's' : ''}).`
                    : `No lessons were created (${failedCount} row${failedCount !== 1 ? 's' : ''} failed). Check court and coach names and daysOfWeek (0–6).`
            );
        } else if (createdCount > 0) {
            toast.success(
                `Created ${createdCount} lesson${createdCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}`
            );
        }
        refreshLessons();
        return { created: createdCount, failed: failedCount };
    };

    const lessonsFormatGuide = (
        <div className="space-y-3 text-xs">
            <div>
                <p className="font-semibold mb-2">Required Columns:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">name</code> - Lesson name</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">lessonType</code> - "group" or "private" (use &quot;clinic&quot; as alias for group). Camps cannot be imported here.</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">daysOfWeek</code> - Comma-separated (0=Sun, 1=Mon, ..., 6=Sat)</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">startTime</code> - HH:MM or HH:MM:SS (e.g., 09:00 or 09:00:00)</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">endTime</code> - Same as startTime</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">maxCapacity</code> - Number (-1 = no limit)</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">price</code> - Decimal number</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">courtNames</code> - Semicolon-separated court names (must exist)</li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">coachNames</code> - Semicolon-separated coach names (must exist)</li>
                </ul>
            </div>
            <div>
                <p className="font-semibold mb-2">Optional columns</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">skillLevel</code> — any, beginner, intermediate, advanced</li>
                    <li>
                        <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ageGroupId</code> — UUID of an academy active age group (
                        preferred for custom groups)
                    </li>
                    <li>
                        <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ageGroup</code> — Either legacy juniors / adults /
                        all, <strong>or</strong> must match an active custom age group label (case-insensitive).
                    </li>
                    <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">isFeatured</code> — true or false</li>
                </ul>
            </div>
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-gray-700 dark:text-gray-300">
                <strong>Example:</strong>
                <br />
                name,lessonType,daysOfWeek,startTime,endTime,maxCapacity,price,skillLevel,ageGroup,ageGroupId,isFeatured,courtNames,coachNames
                <br />
                Junior Red,group,&quot;1,3&quot;,09:00,10:00,8,45,beginner,juniors,,false,&quot;Court 1&quot;,&quot;Coach Smith&quot;
            </div>
        </div>
    );

    if (lessonsLoading || courtsLoading || coachesLoading) {
        return <div className="flex justify-center p-8">Loading lessons...</div>;
    }

    return (
        <div className="mt-6 border-t border-border pt-6 sm:mt-8 sm:pt-8">
            <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl">Term lessons</h2>
                    <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                        <span className="sm:hidden">Lessons for {term.name}</span>
                        <span className="hidden sm:inline">Manage lessons for term: {term.name}</span>
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {viewMode === 'day' && activeCoaches.length > 0 ? (
                        <DayColumnAxisToggle value={dayColumnAxis} onChange={setDayColumnAxis} />
                    ) : null}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                id={TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_VIEWS}
                                variant="outline"
                                size="sm"
                                className="relative"
                                aria-label="Lesson list actions"
                            >
                                <MoreVertical className="h-4 w-4" />
                                {scheduleLocationFilter ? (
                                    <span
                                        className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary"
                                        aria-hidden
                                    />
                                ) : null}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[12rem]">
                            <DropdownMenuItem onClick={handleExportLessons}>
                                <Download className="h-4 w-4 mr-2" />
                                Export
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                                <Upload className="h-4 w-4 mr-2" />
                                Import
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup
                                value={viewMode}
                                onValueChange={(value) => setViewMode(value as TermLessonsViewMode)}
                            >
                                <DropdownMenuRadioItem value="list">
                                    <List className="h-4 w-4 mr-2" />
                                    List view
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="schedule">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    Schedule view
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="day">
                                    <CalendarDays className="h-4 w-4 mr-2" />
                                    Day view
                                </DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                            {hasMultiLocation && activeLocations.length > 0 ? (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger>
                                            <MapPin className="h-4 w-4 mr-2" aria-hidden />
                                            <span className="truncate">Location</span>
                                            <span className="ml-auto pl-2 text-xs text-muted-foreground truncate max-w-[9rem]">
                                                {scheduleLocationFilterLabel}
                                            </span>
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="min-w-[12rem]">
                                            <DropdownMenuRadioGroup
                                                value={scheduleLocationFilter || '__all__'}
                                                onValueChange={(value) =>
                                                    setScheduleLocationFilter(
                                                        value === '__all__' ? '' : value
                                                    )
                                                }
                                            >
                                                <DropdownMenuRadioItem value="__all__">
                                                    All locations
                                                </DropdownMenuRadioItem>
                                                {activeLocations.map((loc) => (
                                                    <DropdownMenuRadioItem key={loc.id} value={loc.id}>
                                                        {loc.name}
                                                    </DropdownMenuRadioItem>
                                                ))}
                                            </DropdownMenuRadioGroup>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                </>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {!showForm && (
                        <Button
                            id={TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_ADD_LESSON}
                            size="sm"
                            onClick={() => openNewLessonForm()}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Lesson
                        </Button>
                    )}
                </div>
            </div>

            <Drawer
                isOpen={showForm}
                onClose={handleCancelEdit}
                onPanelOpen={!editingLesson ? handleLessonCreateDrawerPanelOpen : undefined}
                title={editingLesson ? 'Edit Lesson' : 'Add New Lesson to Term'}
                headerActions={
                    !editingLesson ? (
                        <DrawerTourHelp
                            tourKey="academy-terms-lesson-create"
                            suggestion="New to term lessons? Take a quick tour of this form before you save."
                            buttonLabel="Tour this form"
                        />
                    ) : undefined
                }
                width="3xl"
                footer={
                    <div className="space-y-3">
                        {!isValid && formValidationMessages.length > 0 && (
                            <Alert variant="destructive" className="text-left">
                                <AlertDescription>
                                    <p className="font-medium">Fix these before saving:</p>
                                    <ul className="mt-1 list-disc list-inside space-y-0.5">
                                        {formValidationMessages.map((msg) => (
                                            <li key={msg}>{msg}</li>
                                        ))}
                                    </ul>
                                </AlertDescription>
                            </Alert>
                        )}
                        <div className="flex justify-end space-x-4">
                            <Button type="button" variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                            <Button
                                id={
                                    editingLesson
                                        ? undefined
                                        : TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_SUBMIT
                                }
                                type="submit"
                                form="term-lesson-form"
                                isLoading={submitting || uploadingCover}
                                disabled={!hasValidSubscription || !isValid || uploadingCover}
                            >
                                {editingLesson ? 'Update Lesson' : 'Create Lesson'}
                            </Button>
                        </div>
                    </div>
                }
            >
                <form id="term-lesson-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div
                        id={TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_DETAILS}
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
                    >
                        <div>
                            <label className="block text-sm font-medium mb-1">Lesson Type *</label>
                            <select {...register('lessonType')} className="w-full input">
                                <option value="group">Group Lesson</option>
                                <option value="private">Private Lesson</option>
                            </select>
                        </div>

                        {hasProgramsAccess ? (
                            <Field className="md:col-span-2" data-invalid={!!errors.programId?.message}>
                                <FieldLabel htmlFor="programId" className="flex items-center gap-2">
                                    <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                                    Development program
                                </FieldLabel>
                                <select
                                    id="programId"
                                    aria-invalid={!!errors.programId?.message}
                                    className="w-full input"
                                    value={watchedProgramId}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        applyProgramTemplate(v ? v : null);
                                    }}
                                >
                                    <option value="">None</option>
                                    {[...programsForLessonForm]
                                        .sort((a, b) => {
                                            if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                                            return a.name.localeCompare(b.name);
                                        })
                                        .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                                {!p.isActive ? ' (inactive)' : ''}
                                            </option>
                                        ))}
                                </select>
                                <FieldError>{errors.programId?.message}</FieldError>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {programTemplateHelperText}
                                </p>
                            </Field>
                        ) : (
                            <input type="hidden" {...register('programId')} />
                        )}

                        <Field data-invalid={!!errors.name?.message}>
                            <FieldLabel htmlFor="name">Lesson Name *</FieldLabel>
                            <Input id="name" aria-invalid={!!errors.name?.message} {...register('name')} />
                            <FieldError>{errors.name?.message}</FieldError>
                        </Field>

                        <AcademySportSelectField
                            academyId={currentAcademy?.id}
                            control={control}
                            name="academySportId"
                            required
                            className="md:col-span-2"
                        />

                        {hasMultiLocation && locations.filter((l) => l.isActive).length > 0 && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Location</label>
                                <select {...register('locationId')} className="w-full input">
                                    <option value="">All locations / not specified</option>
                                    {locations
                                        .filter((l) => l.isActive)
                                        .sort((a, b) => a.sortOrder - b.sortOrder)
                                        .map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {loc.name}
                                            </option>
                                        ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    When set, only courts assigned to this location are listed below.
                                </p>
                            </div>
                        )}
                    </div>

                    <div
                        id={TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_SCHEDULE}
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
                    >
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1">Courts *</label>
                            <div className="max-h-32 overflow-y-auto rounded-md border border-input bg-transparent p-2 shadow-sm dark:bg-gray-800">
                                {activeCourtsForPicker.map(court => (
                                    <label key={court.id} className="flex items-center space-x-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedCourtIds.includes(court.id)}
                                            onChange={() => toggleSelection(court.id, 'courtIds')}
                                            className="rounded"
                                        />
                                        <span className="text-sm">{court.name}</span>
                                    </label>
                                ))}
                            </div>
                            {errors.courtIds && <p className="text-xs text-red-500 mt-1">{errors.courtIds.message}</p>}
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium mb-1">Coaches *</label>
                            <div className="max-h-32 overflow-y-auto rounded-md border border-input bg-transparent p-2 shadow-sm dark:bg-gray-800">
                                {activeCoaches.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">No active coaches available</p>
                                ) : (
                                    activeCoaches.map(coach => (
                                      <label key={coach.id} className="flex items-center space-x-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedCoachIds.includes(coach.id)}
                                            onChange={() => toggleSelection(coach.id, 'coachIds')}
                                            className="rounded"
                                        />
                                        <span className="text-sm">{coach.firstName} {coach.lastName}</span>
                                      </label>
                                    ))
                                )}
                            </div>
                            {errors.coachIds && <p className="text-xs text-red-500 mt-1">{errors.coachIds.message}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Days of week * ({selectedDaysOfWeek.length} selected)
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                                {DAYS_OF_WEEK.map((day, i) => (
                                    <label
                                        key={i}
                                        className={`flex items-center justify-center p-2 border rounded-md transition-colors text-sm font-medium cursor-pointer ${
                                            selectedDaysOfWeek.includes(i)
                                                ? 'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-950 dark:border-blue-400 dark:text-blue-200'
                                                : 'bg-white border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={selectedDaysOfWeek.includes(i)}
                                            onChange={() => toggleDaySelection(i)}
                                        />
                                        {day.substring(0, 3)}
                                    </label>
                                ))}
                            </div>
                            {errors.daysOfWeek && (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.daysOfWeek.message}</p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Field data-invalid={!!errors.startTime?.message}>
                                <FieldLabel htmlFor="startTime">Start Time *</FieldLabel>
                                <Input id="startTime" type="time" aria-invalid={!!errors.startTime?.message} {...register('startTime')} />
                                <FieldError>{errors.startTime?.message}</FieldError>
                            </Field>
                            <Field data-invalid={!!errors.endTime?.message}>
                                <FieldLabel htmlFor="endTime">End Time *</FieldLabel>
                                <Input id="endTime" type="time" aria-invalid={!!errors.endTime?.message} {...register('endTime')} />
                                <FieldError>{errors.endTime?.message}</FieldError>
                            </Field>
                        </div>
                    </div>

                    <div
                        id={TERM_MANAGEMENT_TOUR_STEP_IDS.LESSON_FORM_PRICING}
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
                    >
                        <div>
                            <Field data-invalid={!!errors.price?.message}>
                                <FieldLabel htmlFor="price">
                                    Price ({currencySymbol}) *
                                </FieldLabel>
                                <Input
                                    id="price"
                                    type="number"
                                    step="0.01"
                                    aria-invalid={!!errors.price?.message}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    {...register('price', { valueAsNumber: true })}
                                />
                                <FieldError>{errors.price?.message}</FieldError>
                            </Field>
                        </div>

                        {(watchedLessonType === 'group' || watchedLessonType === 'private') && (
                            <div>
                                <Field data-invalid={!!errors.sessionPrice?.message}>
                                    <FieldLabel htmlFor="sessionPrice">
                                        Per-session price ({currencySymbol}){' '}
                                        <span className="text-muted-foreground font-normal">(optional)</span>
                                    </FieldLabel>
                                    <Input
                                        id="sessionPrice"
                                        type="number"
                                        step="0.01"
                                        min={0}
                                        aria-invalid={!!errors.sessionPrice?.message}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        {...register('sessionPrice', {
                                            setValueAs: (v) => {
                                                if (v === '' || v === null || v === undefined) return undefined;
                                                const n = typeof v === 'number' ? v : Number(v);
                                                return Number.isNaN(n) ? undefined : n;
                                            },
                                        })}
                                    />
                                    <FieldError>{errors.sessionPrice?.message}</FieldError>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {watchedLessonType === 'private'
                                            ? 'If set, staff and guardians can enroll for a single session instead of the full term.'
                                            : 'If set, students can book drop-in sessions instead of enrolling for the full term.'}
                                    </p>
                                </Field>
                            </div>
                        )}

                        <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor="maxCapacity" className="block text-sm font-medium">
                                        {watchedLessonType === 'private' ? 'Max students *' : 'Max Capacity *'}
                                    </label>
                                    {watchedLessonType !== 'private' ? (
                                    <label className="flex items-center space-x-2 text-sm">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            checked={watchedMaxCapacity === -1}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setValue('maxCapacity', -1, { shouldValidate: true });
                                                } else {
                                                    setValue('maxCapacity', 6, { shouldValidate: true });
                                                }
                                            }}
                                        />
                                        <span>No student limit</span>
                                    </label>
                                    ) : null}
                                </div>
                                {watchedLessonType === 'private' || watchedMaxCapacity !== -1 ? (
                                    <Field data-invalid={!!errors.maxCapacity?.message}>
                                        <Input
                                            id="maxCapacity"
                                            type="number"
                                            min={1}
                                            max={watchedLessonType === 'private' ? 50 : undefined}
                                            aria-invalid={!!errors.maxCapacity?.message}
                                            onWheel={(e) => e.currentTarget.blur()}
                                            {...register('maxCapacity', { valueAsNumber: true })}
                                        />
                                        <FieldError>{errors.maxCapacity?.message}</FieldError>
                                        {watchedLessonType === 'private' ? (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                One booking per student for this 1-on-1 lesson in the term.
                                            </p>
                                        ) : null}
                                    </Field>
                                ) : (
                                    <div className="p-2 border rounded bg-gray-50 text-gray-500 text-sm italic dark:bg-gray-800 dark:border-gray-700">
                                        Unlimited students allowed
                                    </div>
                                )}
                            </div>

                        {watchedLessonType === 'private' ? (
                            <p className="md:col-span-2 text-xs text-muted-foreground rounded-md border border-border bg-muted/30 p-3">
                                Private lessons in a term run on the selected weekdays for the full program dates
                                (no separate package dates).
                            </p>
                        ) : null}

                        <div>
                            <label className="block text-sm font-medium mb-1">Skill Level *</label>
                            <select {...register('skillLevel')} className="w-full input">
                                <option value="any">Any Level</option>
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        {academyAgeGroups.length > 0 ? (
                            <div>
                                <label className="block text-sm font-medium mb-1">Age Group</label>
                                <select {...register('ageGroupId')} className="w-full input">
                                    <option value="">— None —</option>
                                    {academyAgeGroups.map((ag) => (
                                        <option key={ag.id} value={ag.id}>
                                            {ag.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.ageGroupId && (
                                    <p className="text-xs text-red-500 mt-1">{(errors.ageGroupId as { message?: string }).message}</p>
                                )}
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium mb-1">Age Group *</label>
                                <select {...register('ageGroup')} className="w-full input">
                                    <option value="all">All Ages</option>
                                    <option value="juniors">Juniors</option>
                                    <option value="adults">Adults</option>
                                </select>
                                {errors.ageGroup && (
                                    <p className="text-xs text-red-500 mt-1">{errors.ageGroup.message}</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea {...register('description')} className="w-full input" rows={3} />
                    </div>

                    <div className="md:col-span-2">
                        <LessonCoverImageField
                            displayUrl={coverDisplayUrl}
                            uploading={uploadingCover}
                            disabled={submitting || uploadingCover}
                            inputId="term-lesson-cover-upload"
                            onFileSelect={handleCoverFile}
                            onRemove={handleCoverRemove}
                        />
                        <input type="hidden" {...register('imageUrl')} />
                    </div>

                    {conflicts.length > 0 && (
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-400 rounded-lg flex items-start space-x-3">
                            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                            <div>
                                <h4 className="text-sm font-bold text-yellow-800 dark:text-yellow-200">Scheduling Conflicts Detected</h4>
                                <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 list-disc list-inside">
                                    {conflicts.map((c, i) => (
                                        <li key={i}>
                                            {c.type === 'coachAvailability'
                                                ? `${c.coachName} has no availability on ${(c.missingDays || []).map((d) => DAYS_OF_WEEK[d]).join(', ')} for ${watchedStartTime}-${watchedEndTime}`
                                                : c.type === 'courtHire'
                                                  ? `Court hire on ${c.courtName}${c.courtHireDate ? ` (${c.courtHireDate}${c.courtHireTime ? ` ${c.courtHireTime}` : ''})` : ''} overlaps this schedule`
                                                  : `${c.lesson?.name} (${c.type === 'court' ? 'Court' : 'Coach'} conflict)`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                </form>
            </Drawer>

            <TermLessonStudentsDrawer
                isOpen={!!staffEnrollLesson && !!currentAcademy?.id}
                onClose={closeStaffEnrollDrawer}
                lesson={staffEnrollLesson}
                term={term}
                academyId={currentAcademy?.id ?? ''}
                academySlug={currentAcademy?.slug}
                makeupLessonEnabled={currentAcademy?.makeupLessonEnabled === true}
                onChanged={async () => {
                    await refreshLessons();
                }}
            />

            <div id={TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_LESSONS}>
            {viewMode === 'schedule' ? (
                <TermScheduleView
                    lessons={scheduleViewLessons}
                    term={term}
                    programNameById={programNameById}
                    onLessonClick={offerEditLesson}
                    onTimeSlotClick={handleCreateFromScheduleSlot}
                    onLessonMove={async (lessonId, { dayOfWeek, startTime, endTime }) => {
                        if (!currentAcademy?.id || !hasValidSubscription) return;
                        try {
                            await updateLesson(lessonId, { daysOfWeek: [dayOfWeek], startTime, endTime } as any);
                            toast.success('Lesson moved');
                        } catch (e: unknown) {
                            toast.error(formatSchedulingErrorMessage(e, 'Failed to move lesson'));
                        }
                    }}
                    onLessonCopy={async (sourceLesson, { dayOfWeek, startTime, endTime }) => {
                        if (!currentAcademy?.id || !hasValidSubscription) return;
                        try {
                            const courtIds = getLessonCourtIds(sourceLesson).filter(Boolean);
                            const coachIds = getLessonCoachIds(sourceLesson).filter(Boolean);
                            if (courtIds.length === 0 || coachIds.length === 0) {
                                toast.error('Cannot copy lesson: source lesson must have at least one court and one coach assigned');
                                return;
                            }
                            await createLesson({
                                academyId: currentAcademy.id,
                                termIds: [term.id],
                                courtIds,
                                coachIds,
                                name: sourceLesson.name,
                                description: sourceLesson.description,
                                lessonType: sourceLesson.lessonType,
                                skillLevel: sourceLesson.skillLevel || 'any',
                                ageGroup: sourceLesson.ageGroup || 'all',
                                ageGroupId: sourceLesson.ageGroupId ?? null,
                                daysOfWeek: [dayOfWeek],
                                startTime,
                                endTime,
                                maxCapacity: sourceLesson.maxCapacity,
                                price: sourceLesson.price,
                                currency: sourceLesson.currency,
                                isActive: sourceLesson.isActive ?? true,
                                isFeatured: sourceLesson.isFeatured,
                                programId: sourceLesson.programId ?? null,
                                ...(sourceLesson.locationId
                                    ? { locationId: sourceLesson.locationId }
                                    : {}),
                                ...(sourceLesson.imageUrl
                                    ? { imageUrl: sourceLesson.imageUrl }
                                    : {}),
                            } as any);
                            toast.success('Lesson copied');
                        } catch (e: any) {
                            toast.error(e.message || 'Failed to copy lesson');
                        }
                    }}
                    onLessonDelete={handleDelete}
                    onLessonCancel={handleCancelLesson}
                    onStaffEnroll={hasValidSubscription ? handleStaffEnrollFromSchedule : undefined}
                    coachAvailabilityWarnings={lessonAvailabilityWarnings}
                />
            ) : viewMode === 'day' ? (
                <DayCourtScheduleView
                    dateNavigation="weekday"
                    selectedDayOfWeek={termSelectedDayOfWeek}
                    onDayOfWeekChange={setTermSelectedDayOfWeek}
                    date={termPlanningDate}
                    courts={scheduleCourts}
                    occurrences={termDayOccurrences}
                    academyId={currentAcademy?.id}
                    editable={hasValidSubscription}
                    showCurrentTimeIndicator={false}
                    columnAxis={dayColumnAxis}
                    coaches={coaches}
                    onOccurrenceClick={(occ) => {
                        const lesson = lessons.find((l) => l.id === occ.lessonId);
                        if (lesson) offerEditLesson(lesson);
                    }}
                    onOccurrenceReschedule={handleTermOccurrenceReschedule}
                    onTimeSlotClick={handleCreateFromScheduleSlot}
                />
            ) : lessons.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-gray-500">
                    No lessons added to this term yet.
                </div>
            ) : (
                <DashboardDataCard>
                    <PaginatedTable<Lesson>
                        columns={lessonTableColumns}
                        data={paginatedLessons}
                        pagination={
                            {
                                page: lessonsPage,
                                limit: lessonsPerPage,
                                total: lessons.length,
                                totalPages: lessonsTotalPages,
                            } satisfies PaginationState
                        }
                        emptyMessage="No lessons found."
                        onPageChange={setLessonsPage}
                        onRowClick={offerEditLesson}
                        noWrapperPadding
                    />
                </DashboardDataCard>
            )}
            </div>

            <SessionRescheduleNotifyDialog
                open={!!pendingTermReschedule}
                lessonName={pendingTermReschedule?.occurrence.lessonName ?? 'Lesson session'}
                requireReason={pendingTermRescheduleMeta?.requireReason ?? false}
                showNotifyOption={pendingTermRescheduleMeta?.timeChanged ?? true}
                loading={termRescheduleSaving}
                onCancel={() => setPendingTermReschedule(null)}
                onConfirm={(notifyGuardians, reason) => {
                    if (pendingTermReschedule) {
                        void commitTermReschedule(pendingTermReschedule, notifyGuardians, reason);
                    }
                }}
            />

            <LessonEditRunningScheduleWarningDialog
                open={!!pendingRunningScheduleEditLesson}
                onOpenChange={(open) => {
                    if (!open) setPendingRunningScheduleEditLesson(null);
                }}
                variant={
                    pendingRunningScheduleEditLesson?.lessonType === 'camp' ? 'camp' : 'term_group'
                }
                termName={term.name}
                onContinue={() => {
                    if (pendingRunningScheduleEditLesson) {
                        handleEdit(pendingRunningScheduleEditLesson);
                    }
                    setPendingRunningScheduleEditLesson(null);
                }}
            />

            <CancelTermLessonModal
                isOpen={!!cancelLessonTarget}
                lesson={cancelLessonTarget}
                academyId={currentAcademy?.id ?? ''}
                isLoading={cancelLessonSubmitting}
                variant={
                    cancelLessonTarget?.lessonType === 'private' && !cancelLessonTarget?.termId
                        ? 'package'
                        : 'term'
                }
                makeupLessonEnabled={currentAcademy?.makeupLessonEnabled === true}
                onCancel={() => setCancelLessonTarget(null)}
                onConfirm={handleCancelTermConfirm}
            />

            {/* Import Modal */}
            <ImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImportLessons}
                title="Import Lessons"
                description="Upload a CSV file to import multiple lessons for this term"
                formatGuide={lessonsFormatGuide}
                importingLabel="Importing lessons…"
                requiredColumns={['name', 'lessonType', 'daysOfWeek', 'startTime', 'endTime', 'maxCapacity', 'price', 'courtNames', 'coachNames']}
            />
        </div>
    );
};
